import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, fileUrl, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PHASES, ROLE_LABELS } from "../lib/methodology";
import PhaseStepper from "../components/PhaseStepper";
import { toast } from "sonner";
import {
    ArrowLeft, Save, CheckCircle2, Plus, Trash2, Upload,
    FileText, Users, ListChecks, MessageSquare, FileCheck2, Calendar, Circle, CircleDot, FileDown, Settings, UserPlus, Crown, Mail, CalendarPlus, Send,
} from "lucide-react";
import { buildGoogleCalendarUrl, parseDateMaybe } from "../lib/calendarLinks";

const TABS = [
    { id: "data", label: "Dados", icon: FileText },
    { id: "tasks", label: "Tarefas", icon: ListChecks },
    { id: "sessions", label: "Sessões", icon: Users },
    { id: "evidences", label: "Evidências", icon: FileCheck2 },
];

const TASK_STATUS = {
    pending: { label: "Pendente", icon: Circle, cls: "text-[#52525b]" },
    in_progress: { label: "Em curso", icon: CircleDot, cls: "text-[#b89b6f]" },
    done: { label: "Concluída", icon: CheckCircle2, cls: "text-[#0d1628]" },
};

export default function ProjectDetailPage() {
    const { id } = useParams();
    const { user } = useAuth();
    const [project, setProject] = useState(null);
    const [users, setUsers] = useState([]);
    const [phases, setPhases] = useState([]);
    const [activePhase, setActivePhase] = useState(1);
    const [activeTab, setActiveTab] = useState("data");
    const [formData, setFormData] = useState({});
    const [busy, setBusy] = useState(false);

    const [tasks, setTasks] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [evidences, setEvidences] = useState([]);
    const [notes, setNotes] = useState([]);

    const currentPhaseDef = useMemo(() => PHASES.find((p) => p.num === activePhase), [activePhase]);
    const currentPhaseData = useMemo(() => phases.find((p) => p.phase === activePhase), [phases, activePhase]);
    const canEdit = useMemo(() => {
        if (!user || !project) return false;
        if (user.role === "super_admin") return true;
        if (project.company_id !== user.company_id) return false;
        if (user.role === "collaborator") return (project.assigned_user_ids || []).includes(user.id);
        return true;
    }, [user, project]);

    const reloadProject = useCallback(async () => {
        const [proj, ph] = await Promise.all([
            api.get(`/projects/${id}`),
            api.get(`/projects/${id}/phases`),
        ]);
        setProject(proj.data);
        setPhases(ph.data);
    }, [id]);

    const reloadPhaseSubResources = useCallback(async (phaseNum) => {
        try {
            const [t, s, e, n] = await Promise.all([
                api.get(`/projects/${id}/phases/${phaseNum}/tasks`),
                api.get(`/projects/${id}/phases/${phaseNum}/sessions`),
                api.get(`/projects/${id}/phases/${phaseNum}/evidences`),
                api.get(`/projects/${id}/phases/${phaseNum}/notes`),
            ]);
            setTasks(t.data); setSessions(s.data); setEvidences(e.data); setNotes(n.data);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, [id]);

    useEffect(() => {
        if (!id) return;
        reloadProject().then(() => {
            // initial active phase depends on freshly loaded project
        }).catch((e) => toast.error(formatApiError(e)));
        // load company users for assignee dropdown (if allowed)
        api.get(`/users`).then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
    }, [id, reloadProject]);

    useEffect(() => {
        if (project && activePhase == null) {
            setActivePhase(project.current_phase || 1);
        }
        if (project && !activePhase) {
            setActivePhase(project.current_phase || 1);
        }
    }, [project, activePhase]);

    useEffect(() => {
        if (project && !phases.find((p) => p.phase === activePhase)) {
            // ok, just no data yet
        }
        setFormData(currentPhaseData?.data || {});
        if (id && activePhase) reloadPhaseSubResources(activePhase);
    }, [currentPhaseData, activePhase, id, reloadPhaseSubResources, phases, project]);

    // sync initial phase from project once loaded
    useEffect(() => {
        if (project) setActivePhase((p) => p || project.current_phase || 1);
    }, [project]);

    const savePhase = async (markCompleted = false) => {
        setBusy(true);
        try {
            await api.put(`/projects/${id}/phases/${activePhase}`, {
                phase: activePhase,
                data: formData,
                completed: markCompleted || !!currentPhaseData?.completed,
            });
            await reloadProject();
            toast.success(markCompleted ? "Fase concluída" : "Dados guardados");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    if (!project) return <div className="label-caps">A carregar projeto...</div>;

    const completedMap = project.phase_progress || {};
    const userById = (uid) => users.find((u) => u.id === uid);

    return (
        <div className="space-y-8" data-testid="project-detail-page">
            {/* Header */}
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <Link to="/app/projects" className="label-caps inline-flex items-center gap-2" data-testid="back-to-projects">
                        <ArrowLeft size={12} /> Projetos
                    </Link>
                    <h1 className="font-display text-5xl text-[#0d1628] mt-4" data-testid="project-title">{project.name}</h1>
                    {project.description && <p className="text-[#52525b] mt-3 max-w-3xl">{project.description}</p>}
                </div>
                <a
                    href={`${API}/projects/${id}/report.pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-outline"
                    data-testid="download-pdf-btn"
                >
                    <FileDown size={14} /> Exportar PDF
                </a>
            </div>

            {/* Manager-only: members management */}
            {canEdit && (user?.role === "company_manager" || user?.role === "happiness_manager" || user?.role === "project_manager" || user?.role === "super_admin") && (
                <ProjectMembersPanel project={project} users={users} onUpdated={reloadProject} />
            )}

            {/* Manager-only: phase activation + visibility */}
            {canEdit && (user?.role === "company_manager" || user?.role === "happiness_manager" || user?.role === "project_manager" || user?.role === "super_admin") && (
                <PhaseAccessPanel project={project} onUpdated={reloadProject} />
            )}

            {/* Phase stepper */}
            <div className="border-y border-[#e4e4e7]">
                <PhaseStepper
                    current={activePhase} completed={completedMap} onSelect={setActivePhase}
                    activePhases={user?.role === "collaborator" ? (project.active_phases || [1]) : null}
                />
            </div>

            <section className="grid lg:grid-cols-12 gap-10">
                {/* Sidebar */}
                <aside className="lg:col-span-4">
                    <div className="label-caps">Fase {currentPhaseDef.num}</div>
                    <h2 className="font-display text-4xl text-[#0d1628] mt-3">{currentPhaseDef.title}</h2>
                    <div className="font-display italic text-2xl text-[#b89b6f] mt-2">{currentPhaseDef.verb}</div>
                    <p className="text-[#52525b] mt-6 leading-relaxed">{currentPhaseDef.summary}</p>
                    <div className="mt-6">
                        <div className="label-caps">Conceitos-chave</div>
                        <div className="flex flex-wrap gap-2 mt-3">
                            {currentPhaseDef.concepts.map((c) => (
                                <span key={c} className="text-xs border border-[#0d1628] text-[#0d1628] px-3 py-1.5">{c}</span>
                            ))}
                        </div>
                    </div>
                    {completedMap[String(activePhase)]?.completed && (
                        <div className="flex items-center gap-2 mt-6 text-[#0d1628]" data-testid="phase-completed-badge">
                            <CheckCircle2 size={18} /> <span className="text-sm">Fase concluída</span>
                        </div>
                    )}
                    <Link to={`/app/projects/${id}/surveys/${activePhase}`} className="btn-outline mt-6 inline-flex items-center gap-2 w-full justify-center" data-testid="open-survey-link">
                        <ListChecks size={14} /> Inquérito da fase
                    </Link>

                    <div className="mt-8 pt-6 border-t border-[#e4e4e7]">
                        <div className="label-caps">Resumo da fase</div>
                        <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                            <SummaryStat label="Tarefas" value={tasks.length} />
                            <SummaryStat label="Concluídas" value={tasks.filter((t) => t.status === "done").length} />
                            <SummaryStat label="Sessões" value={sessions.length} />
                            <SummaryStat label="Evidências" value={evidences.length} />
                        </div>
                    </div>
                </aside>

                {/* Main */}
                <div className="lg:col-span-8">
                    {/* Tabs */}
                    <div className="flex flex-wrap gap-1 border-b border-[#e4e4e7] mb-6" data-testid="phase-tabs">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            const active = activeTab === t.id;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setActiveTab(t.id)}
                                    data-testid={`tab-${t.id}`}
                                    className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${active ? "border-[#0d1628] text-[#0d1628]" : "border-transparent text-[#52525b] hover:text-[#0d1628]"}`}
                                >
                                    <Icon size={14} /> {t.label}
                                </button>
                            );
                        })}
                    </div>

                    {activeTab === "data" && (
                        <div className="card-flat" data-testid={`phase-form-${activePhase}`}>
                            <div className="label-caps">Registo de dados</div>
                            <form onSubmit={(e) => { e.preventDefault(); savePhase(false); }} className="mt-6 space-y-6">
                                {currentPhaseDef.fields.map((f) => (
                                    <div key={f.key}>
                                        <label className="label-caps block mb-2">{f.label}</label>
                                        {f.type === "textarea" ? (
                                            <textarea
                                                className="input-clean" rows={3}
                                                value={formData[f.key] || ""}
                                                onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })}
                                                disabled={!canEdit}
                                                data-testid={`field-${f.key}`}
                                            />
                                        ) : f.type === "scale" ? (
                                            <div className="flex items-center gap-2" data-testid={`field-${f.key}`}>
                                                {[1, 2, 3, 4, 5].map((n) => (
                                                    <button type="button" key={n} disabled={!canEdit}
                                                        onClick={() => setFormData({ ...formData, [f.key]: n })}
                                                        className={`phase-dot ${formData[f.key] === n ? "active" : ""}`}>{n}</button>
                                                ))}
                                            </div>
                                        ) : (
                                            <input className="input-clean"
                                                value={formData[f.key] || ""}
                                                onChange={(e) => setFormData({ ...formData, [f.key]: e.target.value })}
                                                disabled={!canEdit}
                                                data-testid={`field-${f.key}`} />
                                        )}
                                        {f.hint && <div className="text-xs text-[#52525b] mt-2">{f.hint}</div>}
                                    </div>
                                ))}
                                {canEdit && (
                                    <div className="flex flex-wrap gap-3 pt-4 border-t border-[#e4e4e7]">
                                        <button type="submit" disabled={busy} className="btn-primary" data-testid="save-phase-btn">
                                            <Save size={16} /> Guardar
                                        </button>
                                        <button type="button" disabled={busy} onClick={() => savePhase(true)} className="btn-outline" data-testid="complete-phase-btn">
                                            <CheckCircle2 size={16} /> Marcar fase como concluída
                                        </button>
                                    </div>
                                )}
                            </form>
                        </div>
                    )}

                    {activeTab === "tasks" && (
                        <TasksTab
                            projectId={id}
                            phase={activePhase}
                            tasks={tasks}
                            users={users}
                            canEdit={canEdit}
                            onChanged={() => reloadPhaseSubResources(activePhase)}
                            userById={userById}
                        />
                    )}

                    {activeTab === "sessions" && (
                        <SessionsTab
                            projectId={id}
                            phase={activePhase}
                            sessions={sessions}
                            users={users}
                            canEdit={canEdit}
                            onChanged={() => reloadPhaseSubResources(activePhase)}
                        />
                    )}

                    {activeTab === "evidences" && (
                        <EvidencesTab
                            projectId={id}
                            phase={activePhase}
                            evidences={evidences}
                            canEdit={canEdit}
                            onChanged={() => reloadPhaseSubResources(activePhase)}
                            userById={userById}
                        />
                    )}

                    {/* Notes / Comments thread (always visible) */}
                    <NotesThread
                        projectId={id}
                        phase={activePhase}
                        notes={notes}
                        canEdit={canEdit}
                        currentUserId={user?.id}
                        currentUserRole={user?.role}
                        onChanged={() => reloadPhaseSubResources(activePhase)}
                    />
                </div>
            </section>
        </div>
    );
}

function SummaryStat({ label, value }) {
    return (
        <div className="border border-[#e4e4e7] px-3 py-2">
            <div className="label-caps text-[10px]">{label}</div>
            <div className="text-xl font-display text-[#0d1628] mt-1">{value}</div>
        </div>
    );
}

// ---------- Tasks ---------------------------------------------------------
function TasksTab({ projectId, phase, tasks, users, canEdit, onChanged, userById }) {
    const [creating, setCreating] = useState(false);
    const [draft, setDraft] = useState({ title: "", description: "", assignee_id: "", due_date: "", start_datetime: "", end_datetime: "", status: "pending", recurrence_freq: "", recurrence_interval: "", recurrence_end: "" });

    const createTask = async (e) => {
        e.preventDefault();
        if (!draft.title.trim()) return;
        setCreating(true);
        try {
            let recurrence = null;
            if (draft.recurrence_freq) {
                recurrence = {
                    frequency: draft.recurrence_freq,
                    end_date: draft.recurrence_end || null,
                };
                if (draft.recurrence_freq === "custom") {
                    const n = Number(draft.recurrence_interval);
                    if (!n || n <= 0) {
                        toast.error("Indique nº de dias > 0 para recorrência personalizada");
                        setCreating(false);
                        return;
                    }
                    recurrence.interval_days = n;
                }
            }
            await api.post(`/projects/${projectId}/phases/${phase}/tasks`, {
                title: draft.title.trim(),
                description: draft.description,
                assignee_id: draft.assignee_id || null,
                due_date: draft.due_date || null,
                start_datetime: draft.start_datetime || null,
                end_datetime: draft.end_datetime || null,
                status: draft.status,
                recurrence,
            });
            setDraft({ title: "", description: "", assignee_id: "", due_date: "", start_datetime: "", end_datetime: "", status: "pending", recurrence_freq: "", recurrence_interval: "", recurrence_end: "" });
            toast.success("Tarefa criada");
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setCreating(false);
        }
    };

    const setStatus = async (taskId, status) => {
        try {
            await api.patch(`/projects/${projectId}/phases/${phase}/tasks/${taskId}`, { status });
            onChanged();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (taskId) => {
        if (!window.confirm("Eliminar tarefa?")) return;
        try {
            await api.delete(`/projects/${projectId}/phases/${phase}/tasks/${taskId}`);
            toast.success("Tarefa eliminada");
            onChanged();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="card-flat" data-testid="tasks-tab">
            <div className="label-caps">Ações & Tarefas</div>

            {canEdit && (
                <form onSubmit={createTask} className="mt-4 grid sm:grid-cols-2 gap-3 pb-6 border-b border-[#e4e4e7]" data-testid="create-task-form">
                    <input className="input-clean sm:col-span-2" placeholder="Título da tarefa"
                        value={draft.title}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        data-testid="new-task-title" required />
                    <textarea className="input-clean sm:col-span-2" rows={2} placeholder="Descrição (opcional)"
                        value={draft.description}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                        data-testid="new-task-description" />
                    <select className="input-clean" value={draft.assignee_id}
                        onChange={(e) => setDraft({ ...draft, assignee_id: e.target.value })}
                        data-testid="new-task-assignee">
                        <option value="">Sem responsável</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                    </select>
                    <div>
                        <label className="label-caps block mb-2 text-[#52525b]">Prazo</label>
                        <input className="input-clean w-full" type="date" value={draft.due_date}
                            onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
                            data-testid="new-task-due" />
                    </div>
                    <div>
                        <label className="label-caps block mb-2 text-[#52525b]">Início (data + hora)</label>
                        <input className="input-clean w-full" type="datetime-local" value={draft.start_datetime}
                            onChange={(e) => setDraft({ ...draft, start_datetime: e.target.value })}
                            data-testid="new-task-start" />
                    </div>
                    <div>
                        <label className="label-caps block mb-2 text-[#52525b]">Fim (data + hora)</label>
                        <input className="input-clean w-full" type="datetime-local" value={draft.end_datetime}
                            onChange={(e) => setDraft({ ...draft, end_datetime: e.target.value })}
                            data-testid="new-task-end" />
                    </div>
                    <div className="sm:col-span-2 grid sm:grid-cols-3 gap-3 pt-3 border-t border-[#e4e4e7]">
                        <div>
                            <label className="label-caps block mb-2 text-[#52525b]">Recorrência</label>
                            <select className="input-clean w-full" value={draft.recurrence_freq}
                                onChange={(e) => setDraft({ ...draft, recurrence_freq: e.target.value })}
                                data-testid="new-task-recurrence-freq">
                                <option value="">Sem recorrência</option>
                                <option value="daily">Diária</option>
                                <option value="weekly">Semanal</option>
                                <option value="monthly">Mensal</option>
                                <option value="yearly">Anual</option>
                                <option value="custom">Personalizada (nº dias)</option>
                            </select>
                        </div>
                        {draft.recurrence_freq === "custom" && (
                            <div>
                                <label className="label-caps block mb-2 text-[#52525b]">Nº de dias</label>
                                <input type="number" min="1" className="input-clean w-full" value={draft.recurrence_interval}
                                    onChange={(e) => setDraft({ ...draft, recurrence_interval: e.target.value })}
                                    data-testid="new-task-recurrence-interval" />
                            </div>
                        )}
                        {draft.recurrence_freq && (
                            <div>
                                <label className="label-caps block mb-2 text-[#52525b]">Fim da recorrência</label>
                                <input type="date" className="input-clean w-full" value={draft.recurrence_end}
                                    onChange={(e) => setDraft({ ...draft, recurrence_end: e.target.value })}
                                    data-testid="new-task-recurrence-end" />
                            </div>
                        )}
                    </div>
                    <div className="sm:col-span-2 flex justify-end">
                        <button type="submit" disabled={creating} className="btn-primary" data-testid="create-task-btn">
                            <Plus size={14} /> Adicionar tarefa
                        </button>
                    </div>
                </form>
            )}

            {tasks.length === 0 ? (
                <div className="text-sm text-[#52525b] mt-6" data-testid="tasks-empty">Sem tarefas registadas nesta fase.</div>
            ) : (
                <ul className="mt-6 space-y-3" data-testid="tasks-list">
                    {tasks.map((t) => {
                        const meta = TASK_STATUS[t.status] || TASK_STATUS.pending;
                        const Icon = meta.icon;
                        const assignee = t.assignee_id ? userById(t.assignee_id) : null;
                        const taskStart = parseDateMaybe(t.start_datetime || t.due_date);
                        const taskEnd = parseDateMaybe(t.end_datetime);
                        const gcalUrl = taskStart ? buildGoogleCalendarUrl({
                            title: `[Tarefa] ${t.title}`,
                            description: t.description || "",
                            start: taskStart,
                            end: taskEnd,
                        }) : null;
                        const icsUrl = `${API}/projects/${projectId}/phases/${phase}/tasks/${t.id}/ics`;
                        const fmtDateTime = (iso) => {
                            const d = parseDateMaybe(iso);
                            if (!d) return null;
                            return d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
                        };
                        return (
                            <li key={t.id} className="border border-[#e4e4e7] p-4" data-testid={`task-item-${t.id}`}>
                                <div className="flex flex-wrap gap-3 items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <Icon size={14} className={meta.cls} />
                                            <div className="font-medium text-[#0d1628]">{t.title}</div>
                                        </div>
                                        {t.description && <div className="text-sm text-[#52525b] mt-2 whitespace-pre-wrap">{t.description}</div>}
                                        <div className="flex flex-wrap gap-3 mt-3 text-xs text-[#52525b]">
                                            {assignee && <span>Resp.: {assignee.name || assignee.email}</span>}
                                            {t.start_datetime && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Calendar size={12} /> {fmtDateTime(t.start_datetime)}
                                                    {t.end_datetime && <> → {fmtDateTime(t.end_datetime)}</>}
                                                </span>
                                            )}
                                            {!t.start_datetime && t.due_date && (
                                                <span className="inline-flex items-center gap-1"><Calendar size={12} /> Prazo: {t.due_date}</span>
                                            )}
                                            <span>Estado: {meta.label}</span>
                                            {t.recurrence && (
                                                <span className="inline-flex items-center gap-1 text-[#b89b6f]" title="Tarefa recorrente" data-testid={`task-recurrence-${t.id}`}>
                                                    ↻ {t.recurrence.frequency === "custom"
                                                        ? `a cada ${t.recurrence.interval_days}d`
                                                        : { daily: "Diária", weekly: "Semanal", monthly: "Mensal", yearly: "Anual" }[t.recurrence.frequency] || "Recorrente"}
                                                    {t.recurrence.end_date && ` até ${t.recurrence.end_date}`}
                                                </span>
                                            )}
                                        </div>
                                        {(taskStart) && (
                                            <div className="flex flex-wrap gap-2 mt-3" data-testid={`task-calendar-${t.id}`}>
                                                {gcalUrl && (
                                                    <a
                                                        href={gcalUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="btn-ghost text-xs"
                                                        data-testid={`task-gcal-${t.id}`}
                                                        title="Adicionar ao Google Calendar"
                                                    >
                                                        <CalendarPlus size={12} /> Google Calendar
                                                    </a>
                                                )}
                                                <a
                                                    href={icsUrl}
                                                    className="btn-ghost text-xs"
                                                    data-testid={`task-ics-${t.id}`}
                                                    title="Descarregar .ics (Outlook, Apple, etc.)"
                                                >
                                                    <FileDown size={12} /> .ics
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                    {canEdit && (
                                        <div className="flex flex-wrap gap-2">
                                            <select
                                                className="input-clean text-xs"
                                                value={t.status}
                                                onChange={(e) => setStatus(t.id, e.target.value)}
                                                data-testid={`task-status-${t.id}`}
                                            >
                                                <option value="pending">Pendente</option>
                                                <option value="in_progress">Em curso</option>
                                                <option value="done">Concluída</option>
                                            </select>
                                            <button type="button" onClick={() => remove(t.id)} className="btn-ghost text-xs" data-testid={`task-delete-${t.id}`}>
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

// ---------- Sessions ------------------------------------------------------
function SessionsTab({ projectId, phase, sessions, users, canEdit, onChanged }) {
    const [creating, setCreating] = useState(false);
    const [draft, setDraft] = useState({ title: "", start_datetime: "", end_datetime: "", location: "", notes: "" });
    const [selectedIds, setSelectedIds] = useState([]);
    const [extraText, setExtraText] = useState("");

    const allSelected = users && users.length > 0 && selectedIds.length === users.length;
    const toggleAll = () => setSelectedIds(allSelected ? [] : (users || []).map((u) => u.id));
    const toggleOne = (uid) => setSelectedIds((cur) => cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]);

    const buildParticipantsString = () => {
        const names = selectedIds
            .map((id) => (users || []).find((u) => u.id === id))
            .filter(Boolean)
            .map((u) => u.name || u.email);
        const extras = extraText.trim();
        return [names.join(", "), extras].filter(Boolean).join(" · ");
    };

    const create = async (e) => {
        e.preventDefault();
        if (!draft.title.trim()) return;
        setCreating(true);
        try {
            await api.post(`/projects/${projectId}/phases/${phase}/sessions`, {
                title: draft.title.trim(),
                start_datetime: draft.start_datetime || null,
                end_datetime: draft.end_datetime || null,
                location: draft.location || "",
                notes: draft.notes,
                participants: buildParticipantsString(),
                participant_ids: selectedIds,
            });
            setDraft({ title: "", start_datetime: "", end_datetime: "", location: "", notes: "" });
            setSelectedIds([]);
            setExtraText("");
            toast.success("Sessão registada");
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setCreating(false);
        }
    };

    const remove = async (sid) => {
        if (!window.confirm("Eliminar sessão?")) return;
        try {
            await api.delete(`/projects/${projectId}/phases/${phase}/sessions/${sid}`);
            toast.success("Sessão eliminada");
            onChanged();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const resendInvites = async (sid) => {
        try {
            const r = await api.post(`/projects/${projectId}/phases/${phase}/sessions/${sid}/send-invites`);
            if (r.data?.ok) {
                toast.success(`Convites enviados a ${r.data.sent} participante(s)`);
            } else {
                toast.info(r.data?.message || "Sem participantes para notificar");
            }
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="card-flat" data-testid="sessions-tab">
            <div className="label-caps">Participantes & Sessões</div>

            {canEdit && (
                <form onSubmit={create} className="mt-4 space-y-4 pb-6 border-b border-[#e4e4e7]" data-testid="create-session-form">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <input className="input-clean sm:col-span-2" placeholder="Título da sessão (workshop, reunião…)"
                            value={draft.title}
                            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                            data-testid="new-session-title" required />
                        <div>
                            <label className="label-caps block mb-2 text-[#52525b]">Início (data + hora)</label>
                            <input className="input-clean w-full" type="datetime-local"
                                value={draft.start_datetime}
                                onChange={(e) => setDraft({ ...draft, start_datetime: e.target.value })}
                                data-testid="new-session-start" required />
                        </div>
                        <div>
                            <label className="label-caps block mb-2 text-[#52525b]">Fim (data + hora)</label>
                            <input className="input-clean w-full" type="datetime-local"
                                value={draft.end_datetime}
                                onChange={(e) => setDraft({ ...draft, end_datetime: e.target.value })}
                                data-testid="new-session-end" />
                        </div>
                        <input className="input-clean sm:col-span-2" placeholder="Local (sala, link Zoom, etc.) — opcional"
                            value={draft.location}
                            onChange={(e) => setDraft({ ...draft, location: e.target.value })}
                            data-testid="new-session-location" />
                    </div>

                    {/* Multi-select participants */}
                    {users && users.length > 0 && (
                        <div data-testid="participants-multiselect">
                            <div className="flex items-center justify-between mb-2">
                                <span className="label-caps text-[#52525b]">Participantes da empresa · {selectedIds.length}/{users.length}</span>
                                <button type="button" onClick={toggleAll}
                                    className="text-xs text-[#0d1628] link-underline" data-testid="participants-select-all">
                                    {allSelected ? "Desmarcar todos" : "Marcar todos"}
                                </button>
                            </div>
                            <div className="max-h-44 overflow-y-auto border border-[#e4e4e7] bg-white p-2 grid sm:grid-cols-2 gap-1">
                                {users.map((u) => (
                                    <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#fdfcf9] px-2 py-1"
                                        data-testid={`participant-option-${u.id}`}>
                                        <input type="checkbox" checked={selectedIds.includes(u.id)} onChange={() => toggleOne(u.id)} />
                                        <span className="text-[#0d1628] truncate">{u.name || u.email}</span>
                                        <span className="text-xs text-[#52525b] truncate ml-auto">{u.role}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <input className="input-clean" placeholder="Outros participantes externos (opcional)"
                        value={extraText}
                        onChange={(e) => setExtraText(e.target.value)}
                        data-testid="new-session-extra-participants" />
                    <textarea className="input-clean" rows={3} placeholder="Notas da sessão"
                        value={draft.notes}
                        onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                        data-testid="new-session-notes" />
                    <div className="flex justify-between items-end flex-wrap gap-3">
                        <p className="text-xs text-[#52525b]">
                            <Mail size={11} className="inline" /> Os participantes selecionados receberão um email com convite e anexo <code>.ics</code>.
                        </p>
                        <button type="submit" disabled={creating} className="btn-primary" data-testid="create-session-btn">
                            <Plus size={14} /> Registar sessão
                        </button>
                    </div>
                </form>
            )}

            {sessions.length === 0 ? (
                <div className="text-sm text-[#52525b] mt-6" data-testid="sessions-empty">Sem sessões registadas nesta fase.</div>
            ) : (
                <ul className="mt-6 space-y-3" data-testid="sessions-list">
                    {sessions.map((s) => {
                        const sStart = parseDateMaybe(s.start_datetime || s.date);
                        const sEnd = parseDateMaybe(s.end_datetime);
                        const fmt = (iso) => {
                            const d = parseDateMaybe(iso);
                            if (!d) return null;
                            return d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
                        };
                        const gcalUrl = sStart ? buildGoogleCalendarUrl({
                            title: `[Sessão] ${s.title}`,
                            description: [s.notes || "", s.participants ? `Participantes: ${s.participants}` : ""].filter(Boolean).join("\n\n"),
                            location: s.location || "",
                            start: sStart,
                            end: sEnd,
                        }) : null;
                        const icsUrl = `${API}/projects/${projectId}/phases/${phase}/sessions/${s.id}/ics`;
                        return (
                            <li key={s.id} className="border border-[#e4e4e7] p-4" data-testid={`session-item-${s.id}`}>
                                <div className="flex flex-wrap gap-3 items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-[#0d1628]">{s.title}</div>
                                        <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#52525b]">
                                            {sStart && (
                                                <span className="inline-flex items-center gap-1">
                                                    <Calendar size={12} /> {fmt(s.start_datetime || s.date)}
                                                    {sEnd && <> → {fmt(s.end_datetime)}</>}
                                                </span>
                                            )}
                                            {s.location && <span>📍 {s.location}</span>}
                                            {s.participants && <span>Participantes: {s.participants}</span>}
                                            {s.participant_ids && s.participant_ids.length > 0 && (
                                                <span className="inline-block bg-[#f5f3ee] px-2 py-0.5">{s.participant_ids.length} pessoa(s) da empresa</span>
                                            )}
                                        </div>
                                        {s.notes && <div className="text-sm text-[#52525b] mt-3 whitespace-pre-wrap">{s.notes}</div>}
                                        {sStart && (
                                            <div className="flex flex-wrap gap-2 mt-3" data-testid={`session-calendar-${s.id}`}>
                                                {gcalUrl && (
                                                    <a href={gcalUrl} target="_blank" rel="noreferrer"
                                                        className="btn-ghost text-xs"
                                                        data-testid={`session-gcal-${s.id}`}
                                                        title="Adicionar ao Google Calendar"
                                                    >
                                                        <CalendarPlus size={12} /> Google Calendar
                                                    </a>
                                                )}
                                                <a href={icsUrl}
                                                    className="btn-ghost text-xs"
                                                    data-testid={`session-ics-${s.id}`}
                                                    title="Descarregar .ics"
                                                >
                                                    <FileDown size={12} /> .ics
                                                </a>
                                                {canEdit && s.participant_ids && s.participant_ids.length > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => resendInvites(s.id)}
                                                        className="btn-ghost text-xs"
                                                        data-testid={`session-resend-${s.id}`}
                                                        title={`Reenviar convites aos ${s.participant_ids.length} participante(s) com email`}
                                                    >
                                                        <Send size={12} /> Reenviar convites
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {canEdit && (
                                        <button type="button" onClick={() => remove(s.id)} className="btn-ghost text-xs" data-testid={`session-delete-${s.id}`}>
                                            <Trash2 size={12} />
                                        </button>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

// ---------- Evidences ----------------------------------------------------
function EvidencesTab({ projectId, phase, evidences, canEdit, onChanged, userById }) {
    const [uploading, setUploading] = useState(false);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 25 * 1024 * 1024) { toast.error("Ficheiro maior que 25MB"); return; }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            await api.post(`/projects/${projectId}/phases/${phase}/evidences`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Evidência carregada");
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const remove = async (eid) => {
        if (!window.confirm("Eliminar evidência?")) return;
        try {
            await api.delete(`/projects/${projectId}/phases/${phase}/evidences/${eid}`);
            toast.success("Evidência eliminada");
            onChanged();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const fmtSize = (b) => b > 1024 * 1024 ? `${(b / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

    return (
        <div className="card-flat" data-testid="evidences-tab">
            <div className="flex flex-wrap gap-3 items-center justify-between">
                <div className="label-caps">Evidências</div>
                {canEdit && (
                    <label className="btn-primary cursor-pointer" data-testid="upload-evidence-btn">
                        <Upload size={14} /> {uploading ? "A carregar…" : "Carregar ficheiro"}
                        <input type="file" className="hidden" onChange={onUpload} disabled={uploading}
                            data-testid="evidence-file-input" />
                    </label>
                )}
            </div>
            <div className="text-xs text-[#52525b] mt-2">Anexa atas, fotos, inquéritos, gráficos ou qualquer ficheiro até 25MB.</div>

            {evidences.length === 0 ? (
                <div className="text-sm text-[#52525b] mt-6" data-testid="evidences-empty">Sem evidências carregadas nesta fase.</div>
            ) : (
                <ul className="mt-6 divide-y divide-[#e4e4e7] border border-[#e4e4e7]" data-testid="evidences-list">
                    {evidences.map((ev) => {
                        const uploader = ev.uploaded_by ? userById(ev.uploaded_by) : null;
                        return (
                            <li key={ev.id} className="px-4 py-3 flex flex-wrap items-center gap-3 justify-between" data-testid={`evidence-item-${ev.id}`}>
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <FileText size={18} className="text-[#b89b6f]" />
                                    <div className="min-w-0">
                                        <a href={fileUrl(ev.storage_path)} target="_blank" rel="noreferrer"
                                            className="text-[#0d1628] hover:underline font-medium truncate block"
                                            data-testid={`evidence-link-${ev.id}`}>{ev.filename}</a>
                                        <div className="text-xs text-[#52525b] mt-1">
                                            {fmtSize(ev.size || 0)} · {String(ev.created_at).slice(0, 10)}
                                            {uploader && <> · por {uploader.name || uploader.email}</>}
                                        </div>
                                    </div>
                                </div>
                                {canEdit && (
                                    <button type="button" onClick={() => remove(ev.id)} className="btn-ghost text-xs" data-testid={`evidence-delete-${ev.id}`}>
                                        <Trash2 size={12} />
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

// ---------- Notes / Comments ---------------------------------------------
function NotesThread({ projectId, phase, notes, canEdit, currentUserId, currentUserRole, onChanged }) {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        setBusy(true);
        try {
            await api.post(`/projects/${projectId}/phases/${phase}/notes`, { text });
            setText("");
            onChanged();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (nid) => {
        try {
            await api.delete(`/projects/${projectId}/phases/${phase}/notes/${nid}`);
            onChanged();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const canDelete = (n) => n.author_id === currentUserId || ["company_manager", "super_admin"].includes(currentUserRole);

    return (
        <div className="mt-8 card-flat" data-testid="notes-thread">
            <div className="flex items-center gap-2">
                <MessageSquare size={16} className="text-[#b89b6f]" />
                <div className="label-caps">Notas internas da fase</div>
            </div>

            {notes.length === 0 ? (
                <div className="text-sm text-[#52525b] mt-4" data-testid="notes-empty">Sem notas registadas.</div>
            ) : (
                <ul className="mt-4 space-y-3" data-testid="notes-list">
                    {notes.map((n) => (
                        <li key={n.id} className="border-l-2 border-[#b89b6f] pl-3" data-testid={`note-item-${n.id}`}>
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-xs text-[#52525b]">
                                    <span className="text-[#0d1628] font-medium">{n.author_name || "Utilizador"}</span>
                                    <span> · {String(n.created_at).slice(0, 16).replace("T", " ")}</span>
                                </div>
                                {canDelete(n) && canEdit && (
                                    <button type="button" onClick={() => remove(n.id)} className="text-xs text-[#52525b] hover:text-[#0d1628]" data-testid={`note-delete-${n.id}`}>
                                        <Trash2 size={12} />
                                    </button>
                                )}
                            </div>
                            <div className="text-sm text-[#0d1628] mt-1 whitespace-pre-wrap">{n.text}</div>
                        </li>
                    ))}
                </ul>
            )}

            {canEdit && (
                <form onSubmit={submit} className="mt-4 flex gap-2" data-testid="new-note-form">
                    <input className="input-clean flex-1" placeholder="Adicionar nota interna…"
                        value={text} onChange={(e) => setText(e.target.value)} data-testid="new-note-input" />
                    <button type="submit" disabled={busy || !text.trim()} className="btn-primary" data-testid="new-note-submit">
                        <Plus size={14} /> Adicionar
                    </button>
                </form>
            )}
        </div>
    );
}


function PhaseAccessPanel({ project, onUpdated }) {
    const [busy, setBusy] = useState(false);
    const activePhases = project.active_phases || [1];
    const visibleToAll = !!project.visible_to_all_company;
    const deadlines = project.survey_deadlines || {};

    const togglePhase = async (phase) => {
        setBusy(true);
        try {
            const next = activePhases.includes(phase)
                ? activePhases.filter((p) => p !== phase)
                : [...activePhases, phase];
            await api.patch(`/projects/${project.id}`, { active_phases: next });
            await onUpdated();
            toast.success(activePhases.includes(phase) ? `Fase ${phase} desativada para colaboradores` : `Fase ${phase} ativada para colaboradores`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const toggleVisibility = async () => {
        setBusy(true);
        try {
            await api.patch(`/projects/${project.id}`, { visible_to_all_company: !visibleToAll });
            await onUpdated();
            toast.success(!visibleToAll ? "Projeto visível a toda a empresa" : "Visibilidade restrita aos membros");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    // Persist the deadline for a specific phase. Accepts an <input type="datetime-local">
    // value (e.g. "2026-03-14T18:30"); an empty string clears the deadline.
    // Values are sent as ISO strings; the backend normalises to UTC.
    const setPhaseDeadline = async (phase, localValue) => {
        setBusy(true);
        try {
            const iso = localValue ? new Date(localValue).toISOString() : null;
            await api.patch(`/projects/${project.id}`, {
                survey_deadlines: { [String(phase)]: iso },
            });
            await onUpdated();
            toast.success(iso ? `Prazo da Fase ${phase} atualizado` : `Prazo da Fase ${phase} removido`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    return (
        <section className="border border-[#e4e4e7] bg-[#fdfcf9] p-5 md:p-6" data-testid="phase-access-panel">
            <div className="flex items-start gap-3 flex-wrap">
                <Settings size={18} className="text-[#b89b6f] mt-1" />
                <div className="flex-1">
                    <div className="label-caps text-[#b89b6f]">Controlo de acesso · Gestores</div>
                    <h3 className="font-display text-2xl text-[#0d1628] mt-1">Visibilidade & Fases ativas</h3>
                    <p className="text-sm text-[#52525b] mt-2 max-w-2xl">
                        Controla quais fases os colaboradores deste projeto podem ver e responder. Fases não ativadas permanecem invisíveis aos colaboradores até as desbloqueares.
                    </p>
                </div>
            </div>

            <div className="mt-5">
                <div className="label-caps mb-2">Visível a toda a empresa</div>
                <label className="flex items-start gap-3 cursor-pointer text-sm">
                    <input
                        type="checkbox" checked={visibleToAll} disabled={busy}
                        onChange={toggleVisibility} className="mt-0.5"
                        data-testid="visible-to-all-toggle"
                    />
                    <span className="text-[#52525b]">
                        Quando ativado, <strong className="text-[#0d1628]">todos os colaboradores da empresa</strong> veem este projeto no menu, mesmo sem serem adicionados como membros. Edição continua restrita aos membros.
                    </span>
                </label>
            </div>

            <div className="mt-6">
                <div className="label-caps mb-3">Fases ativas para colaboradores</div>
                <div className="grid grid-cols-5 gap-2">
                    {[1, 2, 3, 4, 5].map((p) => {
                        const isActive = activePhases.includes(p);
                        return (
                            <button
                                key={p} type="button"
                                onClick={() => togglePhase(p)} disabled={busy}
                                className={`p-3 border text-center transition-colors ${isActive ? "bg-[#0d1628] text-white border-[#0d1628]" : "bg-white text-[#52525b] border-[#e4e4e7] hover:border-[#b89b6f]"}`}
                                data-testid={`phase-active-toggle-${p}`}
                            >
                                <div className="font-display text-2xl">{p}</div>
                                <div className="text-[10px] mt-1 uppercase tracking-wider">{isActive ? "Ativa" : "Inativa"}</div>
                            </button>
                        );
                    })}
                </div>
                <p className="text-xs text-[#52525b] mt-2">
                    Gestores veem todas as fases sempre. As fases inativas ficam ocultas/bloqueadas para colaboradores.
                </p>
            </div>

            <div className="mt-6">
                <div className="label-caps mb-3">Prazo do inquérito · por fase</div>
                <p className="text-xs text-[#52525b] mb-3 max-w-2xl">
                    Define uma data-limite a partir da qual os inquéritos deixam de aceitar respostas. Enquanto o prazo não terminar, os utilizadores podem voltar e editar as suas respostas.
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {[1, 2, 3, 4, 5].map((p) => {
                        const iso = deadlines[String(p)] || "";
                        // Convert stored UTC ISO -> local "YYYY-MM-DDTHH:mm" for the datetime-local input.
                        let localValue = "";
                        if (iso) {
                            try {
                                const d = new Date(iso);
                                if (!Number.isNaN(d.getTime())) {
                                    const pad = (n) => String(n).padStart(2, "0");
                                    localValue = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                                }
                            } catch { /* ignore */ }
                        }
                        return (
                            <div key={p} className="border border-[#e4e4e7] bg-white p-3">
                                <div className="text-xs label-caps mb-2">Fase {p}</div>
                                <input
                                    type="datetime-local"
                                    className="input-clean text-sm w-full"
                                    value={localValue}
                                    disabled={busy}
                                    onChange={(e) => setPhaseDeadline(p, e.target.value)}
                                    data-testid={`phase-deadline-input-${p}`}
                                />
                                {iso && (
                                    <button
                                        type="button"
                                        className="text-xs mt-2 text-[#dc2626] link-underline"
                                        disabled={busy}
                                        onClick={() => setPhaseDeadline(p, "")}
                                        data-testid={`phase-deadline-clear-${p}`}
                                    >
                                        Remover prazo
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function ProjectMembersPanel({ project, users, onUpdated }) {
    const assignedIds = project.assigned_user_ids || [];
    const ownerId = project.owner_id;
    const [busy, setBusy] = useState(false);
    const [invitingId, setInvitingId] = useState(null);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all"); // all | members | available

    // Exclude super_admins from member candidate list
    const eligible = (users || []).filter((u) => u.role !== "super_admin");

    // Memoize counts so the header, filter tabs and bulk actions stay in sync
    const memberCount = useMemo(
        () => eligible.filter((u) => assignedIds.includes(u.id) || u.id === ownerId).length,
        [eligible, assignedIds, ownerId],
    );
    const availableCount = eligible.length - memberCount;

    const filtered = eligible.filter((u) => {
        const matchesSearch = !search || (u.name || "").toLowerCase().includes(search.toLowerCase()) || (u.email || "").toLowerCase().includes(search.toLowerCase());
        if (!matchesSearch) return false;
        const isMember = assignedIds.includes(u.id) || u.id === ownerId;
        if (filter === "members") return isMember;
        if (filter === "available") return !isMember;
        return true;
    });

    const toggleMember = async (uid) => {
        setBusy(true);
        try {
            const next = assignedIds.includes(uid)
                ? assignedIds.filter((x) => x !== uid)
                : [...assignedIds, uid];
            await api.patch(`/projects/${project.id}`, { assigned_user_ids: next });
            await onUpdated();
            toast.success(assignedIds.includes(uid) ? "Membro removido" : "Membro adicionado");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const bulkAddAll = async () => {
        const toAdd = filtered.filter((u) => !assignedIds.includes(u.id)).map((u) => u.id);
        if (toAdd.length === 0) return;
        setBusy(true);
        try {
            await api.patch(`/projects/${project.id}`, { assigned_user_ids: [...assignedIds, ...toAdd] });
            await onUpdated();
            toast.success(`${toAdd.length} membro(s) adicionado(s)`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    // Convenience action for the common "I want the whole company to answer
    // the survey" scenario. Adds every collaborator (role === "collaborator")
    // in the company who is not yet assigned. Ignores the current search /
    // filter so it always acts on the FULL population, matching manager intent.
    const unassignedCollaborators = eligible.filter(
        (u) => u.role === "collaborator" && !assignedIds.includes(u.id),
    );
    const assignAllCollaborators = async () => {
        if (unassignedCollaborators.length === 0) return;
        if (!window.confirm(
            `Atribuir ${unassignedCollaborators.length} colaborador(es) da empresa a este projeto? Vão passar a poder responder aos inquéritos.`,
        )) return;
        setBusy(true);
        try {
            const next = [...assignedIds, ...unassignedCollaborators.map((u) => u.id)];
            await api.patch(`/projects/${project.id}`, { assigned_user_ids: next });
            await onUpdated();
            toast.success(`${unassignedCollaborators.length} colaborador(es) atribuído(s)`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const bulkRemoveAll = async () => {
        const toRemove = filtered.filter((u) => assignedIds.includes(u.id) && u.id !== ownerId).map((u) => u.id);
        if (toRemove.length === 0) return;
        if (!window.confirm(`Remover ${toRemove.length} membro(s) deste projeto?`)) return;
        setBusy(true);
        try {
            const next = assignedIds.filter((id) => !toRemove.includes(id));
            await api.patch(`/projects/${project.id}`, { assigned_user_ids: next });
            await onUpdated();
            toast.success(`${toRemove.length} membro(s) removido(s)`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const sendInvite = async (member) => {
        setInvitingId(member.id);
        try {
            const res = await api.post(`/projects/${project.id}/members/${member.id}/invite`);
            if (res.data?.sent) {
                toast.success(`Convite enviado para ${member.email}`);
            } else {
                toast.warning("SMTP da empresa não configurado — convite não foi enviado");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setInvitingId(null); }
    };

    return (
        <section className="border border-[#e4e4e7] bg-[#fdfcf9] p-5 md:p-6" data-testid="project-members-panel">
            <div className="flex items-start gap-3 flex-wrap">
                <UserPlus size={18} className="text-[#b89b6f] mt-1" />
                <div className="flex-1">
                    <div className="label-caps text-[#b89b6f]">Equipa do projeto</div>
                    <h3 className="font-display text-2xl text-[#0d1628] mt-1">Membros · {memberCount}</h3>
                    <p className="text-sm text-[#52525b] mt-2 max-w-2xl">
                        Selecione os utilizadores da empresa que podem editar este projeto e responder aos inquéritos. Gestores da Empresa e Happiness Managers veem todos os projetos automaticamente — os Gestores de Projeto e Colaboradores só veem os projetos onde estão atribuídos (ou marcados como &quot;visíveis a toda a empresa&quot;).
                    </p>
                </div>
                {unassignedCollaborators.length > 0 && (
                    <button
                        type="button"
                        onClick={assignAllCollaborators}
                        disabled={busy}
                        className="btn-primary text-xs px-3 py-2 whitespace-nowrap"
                        data-testid="members-assign-all-collaborators"
                        title="Atribui todos os colaboradores da empresa a este projeto — passam a poder responder aos inquéritos."
                    >
                        Atribuir todos os colaboradores ({unassignedCollaborators.length})
                    </button>
                )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-5">
                <input
                    className="input-clean" placeholder="Pesquisar nome ou email…"
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    data-testid="members-search"
                />
                <div className="flex gap-1 border border-[#e4e4e7] bg-white" data-testid="members-filter-tabs">
                    {[
                        { v: "all", label: `Todos · ${eligible.length}` },
                        { v: "members", label: `Membros · ${memberCount}` },
                        { v: "available", label: `Disponíveis · ${availableCount}` },
                    ].map((f) => (
                        <button key={f.v} type="button" onClick={() => setFilter(f.v)}
                            data-testid={`members-filter-${f.v}`}
                            className={`flex-1 text-xs py-2 px-2 ${filter === f.v ? "bg-[#0d1628] text-white" : "text-[#52525b] hover:text-[#0d1628]"}`}>
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {filtered.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                    <button type="button" onClick={bulkAddAll} disabled={busy}
                        className="text-xs link-underline text-[#0d1628]" data-testid="members-add-all">
                        Adicionar todos os visíveis ({filtered.filter((u) => !assignedIds.includes(u.id)).length})
                    </button>
                    <span className="text-[#a1a1aa] text-xs">·</span>
                    <button type="button" onClick={bulkRemoveAll} disabled={busy}
                        className="text-xs link-underline text-[#dc2626]" data-testid="members-remove-all">
                        Remover todos os membros visíveis
                    </button>
                </div>
            )}

            <div className="mt-4 max-h-72 overflow-y-auto border border-[#e4e4e7] bg-white" data-testid="members-list">
                {filtered.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#52525b]" data-testid="members-empty">
                        {eligible.length === 0 ? "Sem utilizadores na empresa." : "Nenhum utilizador corresponde ao filtro."}
                    </div>
                ) : (
                    <ul className="divide-y divide-[#e4e4e7]">
                        {filtered.map((u) => {
                            const isMember = assignedIds.includes(u.id) || u.id === ownerId;
                            const isOwner = u.id === ownerId;
                            return (
                                <li key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-[#fdfcf9]"
                                    data-testid={`member-row-${u.id}`}>
                                    <input
                                        type="checkbox" checked={isMember} disabled={busy || isOwner}
                                        onChange={() => toggleMember(u.id)}
                                        data-testid={`member-checkbox-${u.id}`}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm text-[#0d1628] font-medium truncate">{u.name || u.email}</span>
                                            {isOwner && <span className="inline-flex items-center gap-1 text-[10px] bg-[#b89b6f] text-white px-1.5 py-0.5"><Crown size={9} /> Owner</span>}
                                        </div>
                                        <div className="text-xs text-[#52525b] truncate">{u.email}</div>
                                    </div>
                                    {isMember && (
                                        <button type="button" onClick={() => sendInvite(u)} disabled={invitingId === u.id}
                                            title="Enviar/Reenviar convite por email"
                                            className="text-xs text-[#52525b] hover:text-[#0d1628] inline-flex items-center gap-1 disabled:opacity-50"
                                            data-testid={`member-invite-${u.id}`}>
                                            <Mail size={12} /> {invitingId === u.id ? "A enviar..." : "Convite"}
                                        </button>
                                    )}
                                    <span className="text-xs px-2 py-1 bg-[#f5f3ee] text-[#52525b]" data-testid={`member-role-${u.id}`}>
                                        {ROLE_LABELS[u.role] || u.role}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <p className="text-xs text-[#52525b] mt-3">
                O criador do projeto (Owner) é sempre membro e não pode ser removido.
            </p>
        </section>
    );
}

